import { Goal } from 'modules/goals/types';
import { useMemo } from 'react';
import { QueryObserverResult } from 'react-query';
import { ApiError } from 'services/api';
import { RestQuery, useRestQueries } from 'utils/hooks/api';
import { Maybe } from 'utils/tsHelpers';

export type QueryGoalsResponse = Maybe<Goal>;
type QueryGoalsRequest = {
  goalId: string;
};

export type QueryGoalsReturnValue = {
  goals: Goal[];
  isLoading: boolean;
  hasError: boolean;
  refetch: () => Promise<QueryObserverResult<QueryGoalsResponse, ApiError>[]>;
};

const getGoalQueryApiUrl = (goalId: string) => `/i/goals/${goalId}`;

/**
 * NOTE: BE will be working on adding a single endpoint to fetch multiple goals.
 */
export const useQueryGoals = (goalIds: string[]): QueryGoalsReturnValue => {
  const queries: RestQuery<QueryGoalsResponse, QueryGoalsRequest>[] = goalIds.map((goalId) => ({
    url: getGoalQueryApiUrl(goalId),
    params: { goalId },
  }));

  const responses = useRestQueries<QueryGoalsResponse, QueryGoalsRequest>(queries);

  const goalsQueries = responses?.map(({ data, error, isLoading, refetch }) => ({
    goal: data,
    error,
    isLoading,
    refetch,
  }));

  return useMemo(() => {
    return {
      goals: goalsQueries.map((query) => query?.goal).filter(Boolean) as Goal[],
      refetch: () => Promise.all(goalsQueries.map((query) => query?.refetch())),
      isLoading: goalsQueries.some((query) => query?.isLoading),
      hasError: goalsQueries.some((query) => query?.error),
    };
  }, [goalsQueries]);
};
